import Layer from './Layer';
import LayerForeground from './LayerForeground';
import Sky from './Sky';
import FurnitureFactory from './objects/furnitures/FurnitureFactory';
import ItemsFactory from './objects/items/ItemsFactory';
import CraftingController from './objects/CraftingController';
import FirefliesController from './objects/FirefliesController';
import Character from './objects/Character';
import { TIME_DAY, TIME_NIGHT } from 'app/constants/AppConstants';
import Cloud from './objects/Cloud';
import LightController from '../controller/LightController';

export default class Location extends Phaser.Group {
  /* eslint-disable max-statements */
  constructor(game, locationData, sceneWidth, savedData, state) {
    super(game);
    this.locationData = locationData;
    this.sceneWidth = sceneWidth;
    this.state = state; // reference to Campout state

    //
    this.savedData = savedData;
    if (!this.savedData.items) {
      this.savedData.items = locationData.items.concat();
      this.savedData.furnitures = locationData.furnitures.concat();
      this.savedData.characters = locationData.characters.concat();
    }

    //
    this.onShowMacroView = new Phaser.Signal();

    this.furnitureFactory = new FurnitureFactory(this.game, this, this.locationData.furnituresByID);
    this.itemsFactory = new ItemsFactory(this.game, this, this.locationData.itemsByID);
    this.crafting = new CraftingController(this.game, this, this.locationData.recipesByID);
    this.lights = new LightController(this.game, this);

    //
    this.isDraggingScene = false;
    this.pointer = null;
    this.startPointerX = 0;
    this.startSceneX = 0;
    this.controlsEnabled = false;
    this.dragSpeed = 0;

    //
    this.groundY = this.locationData.groundY;

    //
    this.sky = new Sky({ game: this.game, data: locationData.sky });
    this.background = new Layer({
      game: this.game,
      data: locationData.background,
      inputEnabled: true,
    });
    this.midground = this.game.make.group(); // layer with most of the objects and characters
    this.foreground = new LayerForeground({
      game: this.game,
      data: locationData.foreground,
      widht: locationData.width,
    });

    //
    this.addChild(this.sky);
    this.addChild(this.background);
    this.addChild(this.midground);
    this.addChild(this.foreground);

    //
    console.log('PLACE START');
    this.noSave = true;

    //
    this.placeFurnitures();
    this.placeCharacters();
    this.placeItems();

    //
    this.fireflies = new FirefliesController(this.game, this);

    //
    if (savedData.time === TIME_NIGHT) {
      this.setNight(true);
    } else {
      this.setDay();
    }

    console.log('PLACE END');
    this.noSave = false;
  }
  /* eslint-enable */

  get width() {
    return this.locationData.width;
  }

  setCamera(camera) {
    this.camera = camera;
    this.camera.onPosition.add(this.handleCameraOnPosition, this);
    this.savePosition();
  }

  handleCameraOnPosition() {
    this.savePosition();
  }

  // ---------------------------------------------------------------------------
  // INPUT CONTROLS
  // ---------------------------------------------------------------------------
  enableControls() {
    this.controlsEnabled = true;
    this.background.onChildInputDown.add(this.handleInputDown, this);
    this.background.onChildInputUp.add(this.handleInputUp, this);
    this.pointer = null;
  }

  disableControls() {
    this.controlsEnabled = false;
    this.background.onChildInputDown.remove(this.handleInputDown, this);
    this.background.onChildInputUp.remove(this.handleInputUp, this);
    this.pointer = null;
  }

  handleInputDown(sprite, pointer) {
    sprite;
    this.isDraggingScene = true;
    this.pointer = pointer;
    this.prevPointerX = pointer.x;
    this.startPointerX = pointer.x;
    this.startSceneX = this.game.width * 0.5 - this.x;
    this.camera.setPosition(this.startSceneX);
  }

  handleInputUp(sprite, pointer) {
    sprite;
    pointer;
    this.isDraggingScene = false;
    this.pointer = null;

    this.state.scrolled();
  }

  // ---------------------------------------------------------------------------
  // DAY / NIGHT
  // ---------------------------------------------------------------------------
  setDayTime(dayTime) {
    if (dayTime === TIME_DAY) {
      this.setDay();
    } else {
      this.setNight();
    }

    this.saveTime();
  }

  setDay() {
    this.time = TIME_DAY;
    this.lights.setDay();
    this.sky.setDay();
    this.background.setDay();
    this.foreground.setDay();
    for (let i = 0; i < this.furnitures.length; i++) {
      this.furnitures[i].setDay();
    }
    this.fireflies.hideFireflies();

    //
    this.game.sounds.stopAudioSprite('ambience_night', { spriteID: 'sfx' });
    this.game.sounds.playSFX('ambience_day', { spriteID: 'sfx', loop: true });
  }

  setNight(instant = false) {
    this.time = TIME_NIGHT;
    this.lights.setNight(instant);
    this.sky.setNight(instant);
    this.background.setNight(instant);
    this.foreground.setNight(instant);
    for (let i = 0; i < this.furnitures.length; i++) {
      this.furnitures[i].setNight(instant);
    }
    this.fireflies.showFireflies();

    //
    this.game.sounds.stopAudioSprite('ambience_day', { spriteID: 'sfx' });
    this.game.sounds.playSFX('ambience_night', { spriteID: 'sfx', loop: true });
  }

  isDay() {
    return this.time === TIME_DAY;
  }

  // ---------------------------------------------------------------------------
  // CHARACTERS
  // ---------------------------------------------------------------------------
  placeCharacters() {
    this.characters = [];
    for (let i = 0; i < this.savedData.characters.length; i++) {
      const data = this.savedData.characters[i];
      const charData = this.locationData.charactersByID[data.id];
      const char = new Character(this.game, this, data.id, charData);
      char.x = data.x;
      char.y = data.y;
      this.midground.addChild(char);
      this.characters.push(char);

      //
      char.onChange.add(this.saveCharacters, this);

      //
      char.init(data);

      char.updateTransform();

      if (data.in_tent) {
        const tent = this.tents.find((t) => t.id === data.tent);
        tent.addCharacter(char);
      } else if (data.in_pond) {
        this.pond.addInto(char);
      } else {
        this.stopDragCharacter(char);
      }
    }
  }

  stopDragCharacter(character) {
    this.game.sounds.playSFX('generic_down', { spriteID: 'sfx' });

    //
    this.draggedItem = null;
    this.isDraggingItem = false;

    // try to sit character down on furniture
    if (this.trySitDown(character)) return;

    //
    // if (this.tryPutIntoPond(character)) return;

    if (this.tryPutIntoTent(character)) return;

    if (this.tryWearSleepingBag2(character)) return;

    // put character on ground - returns true if added to the furniture top
    if (this.setCharDestPosition(character)) return;

    if (this.tryPutIntoPond(character)) return;
  }

  trySitDown(character) {
    const len = this.furnitures.length;
    for (let i = 0; i < len; i++) {
      const fur = this.furnitures[i];
      const sitPoint = fur.canSit(character.x);
      if (sitPoint) {
        if (character.canSit(sitPoint)) {
          character.sitDown(fur, sitPoint);
          this.moveItemsFromSeat(fur, sitPoint.top);
          return true;
        }
      }
    }
    return false;
  }

  moveItemsFromSeat(furniture, top) {
    if (this.items) {
      furniture.updateTransform();
      furniture.updateBounds();
      const len = this.items.length;
      const bb = new Phaser.Rectangle(furniture.bb.x + top.x, furniture.bb.y + top.y, top.w, top.h);
      // console.log('bb', bb);
      for (let i = 0; i < len; i++) {
        const item = this.items[i];
        item.updateBounds();
        // console.log(item.id, item.worldPosition);
        if (!item.inHand && !item.stack && Phaser.Rectangle.containsPoint(bb, item.worldPosition)) {
          this.setDestinationPosition(item);
        }
      }
    }
  }

  tryPutIntoTent(character) {
    if (character.inSleepingBag) {
      for (let i = 0; i < this.tents.length; i++) {
        const tent = this.tents[i];
        if (tent.canAddCharacter(character.x, character.y)) {
          tent.addCharacter(character);
          this.saveCharacters();
          return true;
        }
      }
    }
    return false;
  }

  /* eslint-disable max-statements */
  setCharDestPosition(char) {
    // get objects under char
    const bb = new Phaser.Rectangle(
      char.worldPosition.x - 1,
      char.worldPosition.y,
      2,
      720 - char.worldPosition.y
    );
    const objects = [];

    // furnitures
    let len = this.furnitures.length;
    for (let i = 0; i < len; i++) {
      const fur = this.furnitures[i];
      fur.updateBounds();
      if (Phaser.Rectangle.intersects(bb, fur.bb)) {
        objects.push(fur);
      }
    }

    // find highest top, and lowest side
    let destObj = null;
    let y0 = 720; // top
    let y1 = char.y; // wall
    len = objects.length;
    for (let i = 0; i < len; i++) {
      const obj = objects[i];

      // top
      const top = obj.getTop(bb);
      if (top && top.character === 'stand' && top.y < y0 && char.y < top.y + top.h) {
        y0 = top.y;
        destObj = obj;
      }

      // side
      const side = obj.getSide(bb);
      if (side && char.y > side.y && char.y < side.y + side.h && side.y + side.h > y1) {
        y1 = side.y + side.h;
      } else if (
        !top &&
        side &&
        char.y > obj.bb.y &&
        char.y < obj.bb.y + obj.bb.height &&
        side.y + side.h > y1
      ) {
        y1 = side.y + side.h;
      }
    }

    // place on the top of object
    const dy = y0 - char.y;
    if (y0 < 720 && dy < 40) {
      // top
      const destY = Math.max(char.y, y0);
      const objY = destObj.y + (destObj.id === 'water_rock' ? 720 : 0);
      const dz = destObj.y - char.y + destObj.dz + 2;
      char.dz = dz;
      char.parentObj = destObj;
      char.destDZ = objY - destY + destObj.dz + 2;
      char.fallDownTo(destY);
      destObj.addObject(char);
      return true;
    } else {
      // side
      y1 = Math.max(y1, this.groundY);
      const dz = y1 - char.y + 1;
      char.dz = dz;
      char.fallDownTo(y1);
    }
    return false;
  }
  /* eslint-enable */

  // ---------------------------------------------------------------------------
  // FURNITURES
  // ---------------------------------------------------------------------------
  placeFurnitures() {
    this.furnitures = [];
    this.tents = [];
    for (let i = 0; i < this.savedData.furnitures.length; i++) {
      const furData = this.savedData.furnitures[i];
      const furniture = this.addFurniture(furData);
      furniture.updateTransform();
      furniture.updateBounds();
    }
  }

  addFurniture(furData) {
    const furniture = this.furnitureFactory.getObject(furData);
    if (furniture.data.layer === 'mg') {
      this.midground.addChildAt(furniture, 0);
    } else if (furniture.data.layer === 'bg') {
      this.background.addObject(furniture);
    } else {
      this.foreground.addObject(furniture);
    }
    furniture.updateBounds();

    //
    if (this.time === TIME_NIGHT) {
      furniture.setNight(true);
      furniture.setLight(0);
    }

    // store furniture
    this.furnitures.push(furniture);
    //

    furniture.onChange.add(this.saveFurnitures, this);

    // store references to some of the furnitures
    switch (furData.id) {
      case 'campfire':
        this.campfire = furniture;
        this.lights.addLight(furniture);
        break;
      case 'composter':
        this.composter = furniture;
        break;
      case 'pond':
        this.pond = furniture;
        this.pond.initLayers();
        break;
      case 'water_rock':
        this.pond.addRock(furniture);
        break;
      case 'tent_1':
      case 'tent_2':
      case 'tent_3':
        this.tents.push(furniture);
        this.moveItemsUnderTent(furniture);
        break;
    }

    return furniture;
  }

  removeFurniture(fur) {
    const ind = this.furnitures.indexOf(fur);
    this.furnitures.splice(ind, 1);
    fur.destroy();
  }

  moveItemsUnderTent(tent) {
    if (this.items) {
      tent.updateTransform();
      tent.updateBounds();
      const len = this.items.length;
      for (let i = 0; i < len; i++) {
        const item = this.items[i];
        item.updateBounds();
        if (!item.inHand && !item.stack && Phaser.Rectangle.intersects(tent.bb, item.bb)) {
          this.setDestinationPosition(item);
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // ITEMS
  // ---------------------------------------------------------------------------
  placeItems() {
    this.items = [];
    for (let i = 0; i < this.savedData.items.length; i++) {
      const itemData = this.savedData.items[i];
      const item = this.addItem(itemData.id, itemData.x, itemData.y, itemData);
      item.updateTransform();
      item.updateBounds();

      // init item with additional data
      item.init(itemData);

      //
      if (itemData.in_pond) {
        this.pond.addInto(item);
      } else if (!itemData.in_hand) {
        this.setDestinationPosition(item);
      }
    }
  }

  addItem(id, x, y) {
    const item = this.itemsFactory.getItem(id);
    const pos = item.data.position; // predefined place position
    if (pos) {
      x = pos.x;
      y = pos.y;
    }
    item.x = x;
    item.y = y;
    this.midground.addChildAt(item, 0);
    item.updateBounds();

    //
    // if (this.time === TIME_NIGHT) {
    //   item.setLight(0);
    // }

    //
    item.onDrop.add(this.handleItemDrop, this);
    item.onChange.add(this.saveItems, this);

    // store slot in object
    this.items.push(item);

    //
    return item;
  }

  removeItem(item) {
    item.onDrop.remove(this.handleItemDrop, this);
    item.onChange.remove(this.saveItems, this);
    const ind = this.items.indexOf(item);
    this.items.splice(ind, 1);
    item.destroy();

    this.saveItems();
  }

  spawnItems(item, toSpawn) {
    for (let i = 0; i < toSpawn.length; i++) {
      const id = toSpawn[i];
      if (id === 'tent_1' || id === 'tent_2' || id === 'tent_3') {
        // place tent furniture
        const fur = this.addFurniture({ id });

        //
        this.saveFurnitures();

        // cloud
        this.addCloud({
          x: fur.x,
          y: fur.y - fur.height * 0.4,
          h: fur.height,
        });
        fur.animateGrowUp();
      } else {
        const spread = item.data.spread ? item.data.spread : 30;
        const dx = Phaser.Math.between(-spread, spread);
        const dy = Phaser.Math.between(0, spread);
        const x = item.worldPosition.x - this.x;
        const y = item.worldPosition.y;
        const newItem = this.addItem(id, x, y);
        newItem.updateTransform();

        if (!this.tryPutIntoPond(newItem)) {
          this.setDestinationPosition(newItem);
        }

        if (!item.hasFlag('same_position')) {
          newItem.animateToPosition(newItem.x + dx, newItem.y + dy, 500, newItem.inPond);
        }

        // loop hint
        if (newItem.data.hint) {
          this.state.setLoopHint(newItem.data.hint);
        }
      }
    }

    // cloud
    this.addCloud({
      x: item.x,
      y: item.y,
      h: item.bb.height,
    });
  }

  spawnItem(item, toSpawn, x, y) {
    const id = toSpawn;

    const dx = Phaser.Math.between(-30, 30);
    const dy = Phaser.Math.between(10, 30);
    const newItem = this.addItem(id, x, y);
    newItem.updateTransform();

    if (this.tryPutIntoPond(newItem)) {
      newItem.animateToPosition(newItem.x + dx, newItem.y + dy, 50);
    } else {
      this.setDestinationPosition(newItem);
      newItem.animateToPosition(newItem.x + dx, newItem.y + dy);
    }

    // cloud
    this.addCloud({
      x: x,
      y: y,
      h: item.bb.height,
    });
  }

  startDragItem(item) {
    //
    this.game.sounds.playSFX('generic_up', { spriteID: 'sfx' });

    //
    this.draggedItem = item;
    this.isDraggingItem = true;

    // if item is in hand, bring it to proper layer
    if (item.inHand) {
      const x = item.worldPosition.x - this.x;
      const y = item.worldPosition.y;
      item.x = x;
      item.y = y;
      item.angle = 0;
      this.midground.addChild(item);
      item.pickupFromHand();
    } else if (item.inPond) {
      const x = item.worldPosition.x - this.x;
      const y = item.worldPosition.y;
      item.x = x;
      item.y = y;
      item.angle = 0;
      this.midground.addChild(item);
      item.pickupFromPond();
    } else if (item.inTent) {
      const x = item.worldPosition.x - this.x;
      const y = item.worldPosition.y;
      item.x = x;
      item.y = y;
      this.midground.addChild(item);
      item.pickupFromTent();
    }
  }

  stopDragItem(item) {
    item;
    this.game.sounds.playSFX('generic_down', { spriteID: 'sfx' });

    //
    this.draggedItem = null;
    this.isDraggingItem = false;
  }

  /* eslint-disable max-statements */
  handleItemDrop(item) {
    // try to eat
    if (this.tryEatItem(item)) return;

    // try to put into hand
    if (item.canBeHold()) {
      const result = this.canPutItemIntoHand(item);
      if (result) {
        result.character.putItemIntoHand(item, result.slot);
        this.saveItems();
        return;
      }

      // update character pose in case item was taken from hand
      const len = this.characters.length;
      for (let i = 0; i < len; i++) {
        this.characters[i].checkPose();
      }
    }

    // try to wear hat
    if (this.tryWearHat(item)) return;

    // try to wear sleeping bag on character
    if (this.tryWearSleepingBag(item)) return;

    //
    if (this.tryPutIntoFire(item)) return;

    //
    if (this.tryPutIntoContainer(item)) return;

    //
    // if (this.tryPutIntoPond(item)) return;

    // try to put into bin
    if (this.tryBinItem(item)) return;

    // try to put into composter
    if (this.tryCompostItem(item)) return;

    // get list of items under/overlapping the released items
    const items = [];
    items.push(item); // add the item to list
    const len = this.items.length;
    for (let i = 0; i < len; i++) {
      const it = this.items[i];
      if (it !== item) {
        it.updateBounds();
        if (Phaser.Rectangle.intersects(item.bb, it.bb)) {
          items.push(it);
        }
      }
    }

    // crafting
    if (!this.tryCraftItem(items, item)) {
      // if not crafted try to stack
      if (!this.tryStack(items, item)) {
        // place on the scene
        if (!this.setDestinationPosition(item)) {
          // put into pond
          this.tryPutIntoPond(item);
        }
      }
    }
  }
  /* eslint-enable */

  tryEatItem(item) {
    if (item.canBeEaten()) {
      const character = this.canPutFoodIntoMouth(item);
      if (character) {
        character.eat(item, item.data.colour);

        // drop item after eating
        if (item.data.drop_on_eat) {
          const len = item.data.drop_on_eat.length;
          for (let i = 0; i < len; i++) {
            const id = item.data.drop_on_eat[i];
            const newItem = this.addItem(id, item.x, item.y);
            const destY = character.y + 5;
            const dz = destY - newItem.y + 1;
            newItem.dz = dz;
            newItem.fallDownTo(destY);
          }
        }

        //
        this.removeItem(item);
        return true;
      }
    }

    return false;
  }

  tryWearHat(item) {
    if (item.isHat()) {
      const character = this.canWearHat(item);
      if (character) {
        character.wearHat(item);
        this.removeItem(item);
        return true;
      }
    }
    return false;
  }

  tryWearSleepingBag(item) {
    if (item.isSleepingBag()) {
      const character = this.canWearSleepingBag(item);
      if (character) {
        character.wearSleepingBag(item);
        this.removeItem(item);
        return true;
      }
    }
    return false;
  }

  tryWearSleepingBag2(character) {
    if (!this.items) return;

    const len = this.items.length;
    for (let i = 0; i < len; i++) {
      const item = this.items[i];
      if (item.isSleepingBag()) {
        item.updateBounds();
        if (character.canWearSleepingBag(item)) {
          character.wearSleepingBag(item);
          this.removeItem(item);
          return true;
        }
      }
    }
    return false;
  }

  tryBinItem(item) {
    // if (item.canBeTrashed()) {
    const len = this.furnitures.length;
    for (let i = 0; i < len; i++) {
      const fur = this.furnitures[i];
      if (fur.isBin) {
        fur.updateBounds();
        if (fur.canPutItemIntoBin(item)) {
          fur.putItemIntoBin(item);
          return true;
        }
      }
    }
    // }

    return false;
  }

  tryPutIntoFire(item) {
    if (item.isFlammable()) {
      if (this.campfire.canAddFuel(item)) {
        // if (Phaser.Rectangle.intersects(item.bb, this.campfire.bb)) {
        this.campfire.addFuel();
        this.removeItem(item);
        return true;
      }
    }
    return false;
  }

  tryPutIntoContainer(item) {
    if (item.canBeStored()) {
      const len = this.items.length;
      for (let i = 0; i < len; i++) {
        const it = this.items[i];
        if (it.isContainer) {
          if (it.canPutItem(item)) {
            // turn off flashlight
            if (item.id === 'flashlight') {
              item.setState(false);
            }
            //
            this.addCloud({ x: item.x, y: item.y, h: it.bb.height }); // add cloud
            it.addItem(item); // add item into container
            return true;
          }
        }
      }
    }

    return false;
  }

  tryCompostItem(item) {
    if (item.canBeComposted()) {
      this.composter.updateBounds();
      if (this.composter.canPutItemInto(item)) {
        this.composter.putItemInto(item);
        return true;
      }
    }

    return false;
  }

  tryPutIntoPond(item) {
    this.pond.updateBounds();
    if (this.pond.isItemOver(item)) {
      // if item can be in water drop it
      this.pond.dropItemInto(item);
      return true;

      // else drop on the edge
      // this.pond.getEdgePosition(item)
      // return true;
    } else if (this.pond.isItemAbove(item)) {
      // check if item can land on edge or in water

      // if item can be in water drop it
      this.pond.dropItemInto(item);
      return true;

      // else drop on the edge
      // this.pond.getEdgePosition(item)
      // return true;
    }

    return false;
  }

  tryCraftItem(items, item) {
    // try to craft new item
    let itemCrafted = false;
    if (items.length > 1) {
      // there are at least 2 items overlapping
      const result = this.crafting.checkItems(items);
      if (result) {
        itemCrafted = true;
        // remove items used to crafting
        for (let i = 0; i < result.toRemove.length; i++) {
          if (result.toRemove[i].stack) {
            // destroy stack
            result.toRemove[i].stack.breakStack();
          }
          this.removeItem(result.toRemove[i]);
        }

        // create new item
        const newItem = this.addItem(result.id, item.x, item.y);
        newItem.updateTransform();
        newItem.animateOnCraft();

        if (!this.tryPutIntoPond(newItem)) {
          this.setDestinationPosition(newItem);
        }

        this.addCloud({ x: item.x, y: newItem.y - newItem.bb.height * 0.3, h: newItem.bb.height });

        this.game.sounds.playSFX('item_combine', { spriteID: 'sfx' });
      }
    }

    return itemCrafted;
  }

  tryStack(items, item) {
    const stackType = item.stackType;
    // return if item not stackable
    if (!stackType || item.hasType('stack') || !item.canStack(stackType)) return false;

    //
    let stacked = false;

    // get all stackable items same type, which are not in stack already
    let stackable = items.filter((i) => {
      return i.canStack(stackType);
    });
    // get all stacks
    const stacks = items.filter((i) => {
      return i.hasType('stack') && i.stackType === stackType;
    });

    // try to make stack
    if (stacks.length > 0) {
      // add item to existing stack
      if (!stacks[0].isFull()) {
        stacks[0].addItem(item);
        stacked = true;
      }
    } else if (stackable.length > 1) {
      // create simple stack
      stackable = stackable.sort((a, b) => b.y - a.y);
      const stack = this.addItem('stack', stackable[0].x, stackable[0].y - 1);
      stack.setStackType(stackType);
      for (let i = 0; i < stackable.length; i++) {
        if (!stack.isFull()) {
          stack.addItem(stackable[i]);
          stacked = true;
        }
      }
      stack.updateTransform();
      //
      if (!this.setDestinationPosition(stack)) {
        // put into pond
        this.tryPutIntoPond(stack);
      }

      this.game.sounds.playSFX('item_combine', { spriteID: 'sfx' });
    }
    return stacked;
  }

  /* eslint-disable max-statements */
  setDestinationPosition(item) {
    // get objects below item
    const bb = new Phaser.Rectangle(
      item.worldPosition.x - 1,
      item.worldPosition.y,
      2,
      720 - item.worldPosition.y
    );
    const objects = [];

    // items
    let len = this.items.length;
    for (let i = 0; i < len; i++) {
      const it = this.items[i];
      if (it !== item) {
        it.updateBounds();
        if (Phaser.Rectangle.intersects(bb, it.bb)) {
          objects.push(it);
        }
      }
    }
    // furnitures
    len = this.furnitures.length;
    for (let i = 0; i < len; i++) {
      const fur = this.furnitures[i];
      fur.updateBounds();
      if (Phaser.Rectangle.intersects(bb, fur.bb)) {
        objects.push(fur);
      }
    }

    // find highest top, and lowest side
    let destObj = null;
    let y0 = 720; // top
    let y1 = item.y; // side
    len = objects.length;
    for (let i = 0; i < len; i++) {
      const obj = objects[i];

      // top
      const top = obj.getTop(bb);
      if (top && top.y < y0 && item.y < top.y + top.h) {
        y0 = top.y;
        destObj = obj;
      }

      // side
      const side = obj.getSide(bb);
      if (side && item.y > side.y && item.y < side.y + side.h && side.y + side.h > y1) {
        y1 = side.y + side.h;
      } else if (
        !top &&
        side &&
        item.y > obj.bb.y &&
        item.y < obj.bb.y + obj.bb.height &&
        side.y + side.h > y1
      ) {
        y1 = side.y + side.h;
      }
    }

    // place on the top of object
    const dy = y0 - item.y;
    if (y0 < 720 && dy < 40) {
      // top
      const destY = Math.max(item.y, y0);
      const objY = destObj.y + (destObj.id === 'water_rock' ? 720 : 0);
      const dz = objY - item.y + destObj.dz + 2;
      item.dz = dz;
      // console.log(dz, destObj.y, destY, destObj.dz, item.y);
      item.parentObj = destObj;
      item.destDZ = objY - destY + destObj.dz + 2;
      destObj.addObject(item);
      item.fallDownTo(destY);
      return true;
    } else {
      // side
      y1 = Math.max(y1, this.groundY);
      const dz = y1 - item.y + 1;
      item.dz = dz;
      item.fallDownTo(y1);
    }

    return false;
  }
  /* eslint-enable */

  canPutItemIntoHand(item) {
    for (let i = 0; i < this.characters.length; i++) {
      const slot = this.characters[i].canPutItemIntoHand(item);
      if (slot) {
        return { character: this.characters[i], slot };
      }
    }
    return null;
  }

  canPutFoodIntoMouth(item) {
    for (let i = 0; i < this.characters.length; i++) {
      if (this.characters[i].canPutFoodIntoMouth(item)) {
        return this.characters[i];
      }
    }
    return null;
  }

  canWearSleepingBag(item) {
    for (let i = 0; i < this.characters.length; i++) {
      if (this.characters[i].canWearSleepingBag(item)) {
        return this.characters[i];
      }
    }
    return null;
  }

  canWearHat(item) {
    for (let i = 0; i < this.characters.length; i++) {
      if (this.characters[i].canWearHat(item)) {
        return this.characters[i];
      }
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // COMMON
  // ---------------------------------------------------------------------------
  setItemsRenderOrder() {
    this.midground.sort('depth', Phaser.Group.SORT_ASCENDING);
  }

  addObjectToLocation(obj) {
    this.midground.addChild(obj);
  }

  showMacroView({ x, y, macroView, furniture, animInd }) {
    this.onShowMacroView.dispatch({ x, y, macroView, furniture, animInd });
  }

  /* eslint-disable max-statements */
  step(dt) {
    // update lights
    this.lights.checkLights();

    // update items
    let len = this.items.length;
    for (let i = 0; i < len; i++) {
      this.items[i] && this.items[i].step(dt);
    }
    len = this.characters.length;
    for (let i = 0; i < len; i++) {
      this.characters[i].step(dt);
    }
    len = this.furnitures.length;
    for (let i = 0; i < len; i++) {
      this.furnitures[i].step(dt);
    }

    //
    if (this.isDraggingScene && this.pointer) {
      this.dragSpeed = -(this.pointer.x - this.prevPointerX) / dt;
      this.dragSpeed = Phaser.Math.clamp(this.dragSpeed, -1000, 1000);
      const dx = this.pointer.x - this.startPointerX;
      this.camera.destX = this.startSceneX - dx;
      this.prevPointerX = this.pointer.x;
    } else if (this.dragSpeed !== 0) {
      this.camera.destX += this.dragSpeed * dt;
      this.dragSpeed -= this.dragSpeed * dt * 4;
      if (Math.abs(this.dragSpeed) < 3) {
        this.dragSpeed = 0;
      }
    }

    this.setItemsRenderOrder();

    // check if item can be placed to hero hand
    if (this.isDraggingItem && this.draggedItem.isItem && this.draggedItem.canBeHold()) {
      len = this.characters.length;
      for (let i = 0; i < len; i++) {
        this.characters[i].checkIfItemIsClose(this.draggedItem);
      }
    }

    // check if item can be eaten
    if (this.isDraggingItem && this.draggedItem.isItem && this.draggedItem.canBeEaten()) {
      len = this.characters.length;
      for (let i = 0; i < len; i++) {
        this.characters[i].checkIfItemIsCloseToMouth(this.draggedItem);
      }
    }

    // scroll screen with dragged item
    if (this.isDraggingItem && this.controlsEnabled) {
      const left = 150;
      const right = this.game.width - left;
      const x = this.draggedItem.pointer.x;
      const speed = 720 * dt;
      if (x < left) {
        // scroll left
        if (this.x < 0) {
          // can scroll left
          const ratio = 1 - x / 150;
          this.camera.destX -= speed * ratio;
        }
      } else if (x > right) {
        // scroll right
        if (this.x > -this.sceneWidth + this.game.width) {
          // can scroll right
          const ratio = (x - right) / 150;
          this.camera.destX += speed * ratio;
        }
      } else {
        const x = this.game.width * 0.5 - this.x;
        this.camera.destX = x;
      }
    }

    // paralax effect
    const offset = this.x / (this.width - this.game.width);
    this.sky.x = offset * (this.sky.width - this.width);
    // this.foreground.x = offset * (this.foreground.width - this.width);
  }
  /* eslint-enable */

  getForegrounOffsetForX(x) {
    const offset = x / (this.width - this.game.width);
    return offset * (this.foreground.width - this.width);
  }

  addCloud({ x, y, h }) {
    const size = Phaser.Math.clamp(h / 200, 0.4, 1);
    const cloud = new Cloud(this.game, x, y, size);
    this.midground.addChild(cloud);
  }

  // for onboarding
  pulseTents() {
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i];
      if (
        item.id === 'tent_1_rolled' ||
        item.id === 'tent_2_rolled' ||
        item.id === 'tent_3_rolled'
      ) {
        item.pulse(2000);
      }
    }
  }

  // serialization
  serializeItems() {
    const items = [];
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i];
      const itemData = item.serialize();
      if (itemData) {
        items.push(itemData);
      }
    }

    return items;
  }

  serializeFurnitures() {
    const furnitures = [];
    for (let i = 0; i < this.furnitures.length; i++) {
      const furniture = this.furnitures[i];
      const furnitureData = furniture.serialize();
      if (furnitureData) {
        furnitures.push(furnitureData);
      }
    }

    return furnitures;
  }

  serializeCharacters() {
    const characters = [];
    for (let i = 0; i < this.characters.length; i++) {
      const character = this.characters[i];
      const characterData = character.serialize();
      characters.push(characterData);
    }

    return characters;
  }

  saveCharacters(msg) {
    msg;
    // console.log(msg);
    if (this.noSave) return;
    // console.log('saveCharacters');
    this.savedData.characters = this.serializeCharacters();
    this.game.controller.saves.save({ data: this.savedData });
  }

  saveFurnitures() {
    if (this.noSave) return;
    // console.log('saveFurnitures');
    this.savedData.furnitures = this.serializeFurnitures();
    this.game.controller.saves.save({ data: this.savedData });
  }

  saveItems() {
    if (this.noSave) return;
    // console.log('saveItems');
    this.savedData.items = this.serializeItems();
    this.game.controller.saves.save({ data: this.savedData });
  }

  savePosition() {
    this.savedData.start_point = this.camera.currentX;
    this.game.controller.saves.save({ data: this.savedData });
  }

  saveTime() {
    this.savedData.time = this.time;
    this.game.controller.saves.save({ data: this.savedData });
  }
}
